import { useEffect, useRef, useState } from 'react';
import { TRACKER_IDS, TRACKER_NAMES, type Payload, type TrackerId, type WindowKey } from '../api/types';
import { count, gapStep, median, pct, spreadOf, usd } from '../lib/format';
import { Donut, HBars, Rail, finite, type Reading } from '../ui/charts';
import { GlassBars, GlassHBars } from '../ui/glass';
import { Icon } from '../ui/Icon';
import { Count } from '../ui/motion';

type Src = 'fairgambling' | 'tanzanite';

const WLONG: Record<WindowKey, string> = { '1d': '24 hours', '7d': '7 days', '14d': '14 days', '30d': '30 days', '90d': '90 days', '365d': 'year' };
/** "A, B and C". */
const nameList = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** Market-wide daily deposits: the analytics feed's six largest casinos, summed per day. */
function useDaily(src: Src, win: WindowKey) {
  const [points, setPoints] = useState<{ day: string; value: number | null }[] | null>(null);
  useEffect(() => {
    const ctl = new AbortController();
    setPoints(null);
    fetch(`/api/casinobias/analytics?source=${src}&w=${win}&casino=all&chain=all&token=all`, { signal: ctl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { series?: { points: { day: string; deposits: number | null }[] }[] }) => {
        const byDay = new Map<string, number>();
        for (const s of d.series ?? []) for (const p of s.points) if (finite(p.deposits)) byDay.set(p.day, (byDay.get(p.day) ?? 0) + p.deposits);
        setPoints([...byDay.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([day, value]) => ({ day, value })));
      })
      .catch(() => { if (!ctl.signal.aborted) setPoints([]); });
    return () => ctl.abort();
  }, [src, win]);
  return points;
}

/* The hero plays a 7-second loop made with Higgsfield (Grok Video, started
   from HouseGap's own still, public/img/src/hero.png). The model pushed the
   camera in over its first second, so the loop is the steady second half,
   played forward then back so it never jumps. Its first frame is the poster
   and the card's background, so the fade-in never changes the picture. It
   is muted and decorative, and a looping video can always be paused. */
function useHeroVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { void v.play().catch(() => undefined); setPaused(false); } else { v.pause(); setPaused(true); }
  };
  const video = (
    <video ref={ref} className={`hero-video${playing ? ' playing' : ''}`} autoPlay muted loop playsInline preload="auto"
      poster="/img/hero-loop-poster.webp" aria-hidden onPlaying={() => setPlaying(true)}>
      <source src="/img/hero-loop.webm" type="video/webm" />
      <source src="/img/hero-loop.mp4" type="video/mp4" />
    </video>
  );
  return { video, paused, toggle };
}

export function Dashboard({ data, win }: { data: Payload; win: WindowKey }) {
  const [src, setSrc] = useState<Src>('fairgambling');
  const daily = useDaily(src, win);
  const hero = useHeroVideo();

  const a = data.analysis.volume[win];
  if (!a) return <div className="tile"><p className="empty">Nothing published for this window.</p></div>;

  const counted = a.consensusSources;
  // Matched totals: every counted tracker measured over the same casino list,
  // which is the only comparison that means anything.
  const readings: Reading[] = TRACKER_IDS.flatMap((id) => {
    const v = a.commonTotals?.[id];
    return finite(v) && counted.includes(id) ? [{ id, value: v, counted: true }] : [];
  });
  const values = readings.map((r) => r.value);
  const mid = median(values);
  const gap = spreadOf(values);

  const cross = a.rows.filter((r) => r.consensusBy.length >= 2).length;
  const share = a.rows.length ? (cross / a.rows.length) * 100 : 0;

  const gaps = a.rows
    .filter((r) => r.consensusBy.length > 1 && finite(r.spreadPct))
    .sort((x, y) => (y.spreadPct ?? 0) - (x.spreadPct ?? 0));
  const widest = gaps[0];
  const largest = a.rows.filter((r) => finite(r.consensus)).slice(0, 6);

  const status = (id: TrackerId) => data.sources.find((s) => s.id === id);

  return (
    <>
      <div className="dash">
        {/* -------------------------------------------------------- hero */}
        <section className="hero-tile has-video">
          {hero.video}
          <div className="hero-top">
            <span><i className="status-dot" aria-hidden /> {counted.length} trackers counted · read hourly</span>
            <span className="hero-top-end">
              {a.label ?? win} window
              <button type="button" className="hero-motion" onClick={hero.toggle} aria-pressed={hero.paused} aria-label={hero.paused ? 'Play background video' : 'Pause background video'}>
                {hero.paused
                  ? <svg viewBox="0 0 24 24" aria-hidden><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
                  : <svg viewBox="0 0 24 24" aria-hidden><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor" /></svg>}
              </button>
            </span>
          </div>

          <div className="hero-copy">
            {/* The headline names who disagrees, and the line under it gives the
                two figures that make the gap, so the claim is checkable at a glance. */}
            <h1 className="hero-title">
              {finite(gap) ? <>Casino trackers disagree by <Count value={gap} format={(v) => pct(v, 0)} /></> : 'Casino trackers, side by side'}
            </h1>
            <p className="hero-sub">
              {readings.length >= 2
                ? <>Over the same {WLONG[win]} and the same {a.commonCount ?? 0} casinos, {nameList(readings.map((r) => TRACKER_NAMES[r.id]))} put deposits anywhere between <b>{usd(Math.min(...values))}</b> and <b>{usd(Math.max(...values))}</b>. HouseGap shows where they split, casino by casino.</>
                : <>Public trackers each publish deposit figures for crypto casinos. HouseGap lines them up casino by casino and shows where they split.</>}
            </p>

            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-icon" aria-hidden><Icon name="casinos" /></span>
                <div><b><Count value={a.rows.length} format={count} /></b><small>casinos</small></div>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-icon" aria-hidden><Icon name="rings" /></span>
                <div><b>{a.commonCount ?? 0}</b><small>on every tracker</small></div>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-icon" aria-hidden><Icon name="overview" /></span>
                <div><b><Count value={mid} format={usd} /></b><small>median deposits</small></div>
              </div>
            </div>

            <div className="hero-actions">
              <a className="btn btn-white btn-lg" href={`/casinos?w=${win}`}>Explore casinos</a>
              <a className="btn btn-ghost btn-lg" href={`/trackers?w=${win}`}>How it works</a>
            </div>

            <div className="hero-endpoints">
              <div className="hero-endpoints-head">
                <span>The trackers</span>
                <a href={`/trackers?w=${win}`}>Method ↗</a>
              </div>
              <div className="ep-tiles">
                {TRACKER_IDS.map((id) => {
                  const s = status(id);
                  const total = a.totals?.[id]?.value;
                  const ok = !!s?.ok && !s?.stale;
                  const role = counted.includes(id) ? 'counted' : 'reference';
                  // Four tiles share the hero's width, so each says one thing up
                  // top: stale if it is, otherwise its role. The title has both.
                  return (
                    <a key={id} className={`ep-tile${ok ? '' : ' is-stale'}`} href={`/trackers?w=${win}`}
                      title={`${TRACKER_NAMES[id]}: ${role === 'counted' ? 'counted toward the median' : 'reference only'}${ok ? '' : ', data is stale'}`}>
                      <span className="ep-tile-top">
                        <i className="ep-swatch" style={{ background: `var(--tracker-${id})` }} aria-hidden />
                        {ok ? role : 'stale'}
                      </span>
                      <span>
                        <b>{finite(total) ? usd(total) : '—'}</b>
                        <small>{TRACKER_NAMES[id]}</small>
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* -------------------------------------------------- the spread */}
        <section className="tile ping-tile">
          <div className="tile-head">
            <h2 className="tile-title">How far apart</h2>
            <span className="tile-note">same {a.commonCount ?? 0} casinos</span>
          </div>
          <div className="big-figure">
            <b><Count value={gap} format={(v) => pct(v)} /></b>
            <span>between the highest and the lowest tracker</span>
          </div>
          <Rail readings={readings} />
        </section>

        {/* ---------------------------------------------------- coverage */}
        <section className="tile mix-tile">
          <div className="tile-head">
            <h2 className="tile-title">Coverage</h2>
            <span className="tile-note">cross-checkable</span>
          </div>
          <Donut share={share} label="on 2+ trackers" />
          <ul className="kv">
            <li><span>{cross}</span><span>2+ sources</span></li>
            <li><span>{a.rows.length - cross}</span><span>one only</span></li>
            <li><span>{counted.length}</span><span>counted</span></li>
          </ul>
        </section>

        {/* ----------------------------------------------------- daily */}
        <section className="tile chart-tile">
          <div className="tile-head">
            <h2 className="tile-title">Daily deposits</h2>
            <div className="seg seg-sm" role="group" aria-label="Source">
              <button aria-pressed={src === 'fairgambling'} onClick={() => setSrc('fairgambling')}>FairGambling</button>
              <button aria-pressed={src === 'tanzanite'} onClick={() => setSrc('tanzanite')}>Tanzanite</button>
            </div>
          </div>
          {daily === null
            ? <p className="empty">Loading the daily series…</p>
            : <GlassBars points={daily} format={usd} color={`var(--tracker-${src})`} height={140} />}

          <div className="metric-row">
            <div className="metric">
              <span className="metric-icon" aria-hidden><Icon name="coins" /></span>
              <b>{usd(mid)}</b><small>matched<br />deposits</small>
            </div>
            <div className="metric">
              <span className="metric-icon" aria-hidden><Icon name="pulse" /></span>
              <b>{finite(gap) ? pct(gap, 0) : '—'}</b><small>trackers<br />apart</small>
            </div>
            <a className="metric" href={widest ? `/casino?c=${widest.key}&w=${win}` : `/casinos?w=${win}`}>
              <span className="metric-icon" aria-hidden><Icon name="warning" /></span>
              <b>{widest ? pct(widest.spreadPct, 0) : '—'}</b><small>widest:<br />{widest?.name ?? 'none'}</small>
            </a>
          </div>

          <div className="banner">
            <Icon name="info" />
            <span><b>Missing is not zero.</b> A tracker that does not list a casino is left out of its median.</span>
            <a href={`/trackers?w=${win}`}>Method</a>
          </div>
        </section>
      </div>

      {/* ------------------------------------------------------ row two */}
      <div className="grid-3">
        <section className="tile">
          <div className="tile-head">
            <h2 className="tile-title">Widest gaps</h2>
            <a className="tile-note" href={`/casinos?w=${win}&only=gap`}>All →</a>
          </div>
          <GlassHBars items={gaps.slice(0, 6).map((r) => ({
            key: r.key, href: `/casino?c=${r.key}&w=${win}`,
            label: r.name, value: r.spreadPct ?? 0, display: pct(r.spreadPct, 0),
            className: `g${gapStep(r.spreadPct)}`,
          }))} />
        </section>

        <section className="tile">
          <div className="tile-head">
            <h2 className="tile-title">Largest casinos</h2>
            <span className="tile-note">bar = median · dot = tracker</span>
          </div>
          <HBars items={largest.map((r) => ({
            key: r.key, href: `/casino?c=${r.key}&w=${win}`,
            label: r.name, value: r.consensus ?? 0, display: usd(r.consensus),
            dots: TRACKER_IDS.flatMap((id) => {
              const v = r.by[id]?.value;
              return finite(v) ? [{ key: id, value: v, color: `var(--tracker-${id})`, title: `${TRACKER_NAMES[id]} ${usd(v)}`, hollow: !r.consensusBy.includes(id) }] : [];
            }),
          }))} />
        </section>

        <section className="tile">
          <div className="tile-head">
            <h2 className="tile-title">Deposits by tracker</h2>
            <span className="tile-note">{a.label ?? win} · all coverage</span>
          </div>
          <GlassHBars items={TRACKER_IDS.flatMap((id) => {
            const v = a.totals?.[id]?.value;
            return finite(v) ? [{ key: id, value: v, display: usd(v), color: `var(--tracker-${id})`, label: TRACKER_NAMES[id] }] : [];
          })} />
          <p className="tile-foot">Each tracker covers a different casino list, which explains part of this gap. The spread above uses the shared list only.</p>
        </section>
      </div>
    </>
  );
}
